import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { Grid } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { Button, CollapseCard } from '@totalsoft/rocket-ui';
import AssetSummary from './AssetSummary';
import AssetComponent from './AssetComponent';
import AssetEquipmentComponent from './AssetEquipmentComponent';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import FakeTextWrapper from 'components/common/FakeTextWrapper';

const AssetCard = ({
  loadingStatus,
  assetLens,
  summaryData,
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  currencyCode,
  engineTypes,
  assetTypes,
  registrationTypes,
  assetCategories,
  assetSubcategories,
  vehicleCategories,
  hierarchy,
  hierarchyLevelsSources,
  onCategoryChange,
  onAssetTypeChange,
  onHierarchyLevelChange,
  onOtherAssetNameChange,
  onChassisNumberChange,
  onAssetPropertyBlur,
  onSave,
  saveDisabled,
  saving,
  validation,
  equipmentLens,
  onSelectEquipment,
  validateCascoProperties,
  readOnly
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditVehicle = useAuthorization(AuthorizationCodes.hasRightToEditVehicle, documentTypeId, documentStateId);
  const { t } = useTranslation();

  return (
    <CollapseCard
      id='assetCard_CollapseCard'
      icon={DirectionsCarIcon}
      title={t('Offer.Edit.Asset.Title')}
      toggleOnHeaderClick
      subheader={<AssetSummary summaryData={summaryData} loading={loadingStatus.summaryLoading} assetTypes={assetTypes} />}
      hideSubheaderOnExpand
    >
      <Grid container spacing={3}>
        {loadingStatus.assetLoading || !insuranceTypeCode ? (
          <FakeTextWrapper sx={{ ml: 3, mt: 2 }} />
        ) : (
          <Grid item container xs={12}>
            <AssetComponent
              assetLens={assetLens}
              loadingStatus={loadingStatus}
              insuranceTypeCode={insuranceTypeCode}
              rcaInsuranceTypeCode={rcaInsuranceTypeCode}
              cascoInsuranceTypeCode={cascoInsuranceTypeCode}
              currencyCode={currencyCode}
              engineTypes={engineTypes}
              assetTypes={assetTypes}
              registrationTypes={registrationTypes}
              assetCategories={assetCategories}
              assetSubcategories={assetSubcategories}
              vehicleCategories={vehicleCategories}
              hierarchy={hierarchy}
              hierarchyLevelsSources={hierarchyLevelsSources}
              onCategoryChange={onCategoryChange}
              onAssetTypeChange={onAssetTypeChange}
              onHierarchyLevelChange={onHierarchyLevelChange}
              onOtherAssetNameChange={onOtherAssetNameChange}
              onChassisNumberChange={onChassisNumberChange}
              onAssetPropertyBlur={onAssetPropertyBlur}
              onSave={onSave}
              saveDisabled={saveDisabled}
              validation={validation}
              validateCascoProperties={validateCascoProperties}
              readOnly={readOnly}
            />
            {insuranceTypeCode === cascoInsuranceTypeCode && (
              <AssetEquipmentComponent
                equipmentLens={equipmentLens}
                onSelectEquipment={onSelectEquipment}
                loading={loadingStatus.equipmentLoading}
                readOnly={readOnly('Asset.Equipment', hasRightToEditVehicle)}
              />
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <Button id='Asset_Save' color='primary' onClick={onSave} disabled={saveDisabled || !hasRightToEditVehicle}>
            {saving ? t('General.Saving') : t('General.Save')}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

AssetCard.propTypes = {
  loadingStatus: PropTypes.object.isRequired,
  assetLens: PropTypes.object.isRequired,
  summaryData: PropTypes.object,
  insuranceTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  currencyCode: PropTypes.string,
  engineTypes: PropTypes.object,
  assetTypes: PropTypes.array,
  registrationTypes: PropTypes.array,
  assetCategories: PropTypes.array,
  assetSubcategories: PropTypes.array,
  vehicleCategories: PropTypes.array,
  hierarchy: PropTypes.array,
  hierarchyLevelsSources: PropTypes.object,
  onCategoryChange: PropTypes.func.isRequired,
  onAssetTypeChange: PropTypes.func.isRequired,
  onHierarchyLevelChange: PropTypes.func.isRequired,
  onOtherAssetNameChange: PropTypes.func.isRequired,
  onChassisNumberChange: PropTypes.func.isRequired,
  onAssetPropertyBlur: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  validation: PropTypes.object,
  equipmentLens: PropTypes.object,
  onSelectEquipment: PropTypes.func.isRequired,
  validateCascoProperties: PropTypes.bool.isRequired,
  readOnly: PropTypes.func.isRequired
};
export default AssetCard;
